/**
 * CAOS-360 — creating and changing a record from the workspace.
 *
 * Until now every Sales surface was read-only: a list you could look at and a record page with not
 * one control on it. Everything the workspace showed was data that got there some other way. The
 * platform could already WRITE — the report-a-bug form does exactly that — so what was missing was
 * never the write, it was any surface that offered it.
 *
 * Two rules this module exists to keep:
 *
 *  1. NEVER OFFER WHAT WOULD BE DROPPED. The write boundary strips any field the caller may not
 *     write, silently and correctly — which means a form that offers a field somebody cannot write
 *     lets them type a value, press save, and watch it vanish with no error. So the form asks the
 *     Engine what THIS person may change (`data/describe`, resolved by the same access engine the
 *     write enforces with) and offers exactly that.
 *  2. NEVER LEAVE A LIE ON THE SCREEN. If a save fails, the value goes back to what it was and the
 *     failure is said out loud. A control still showing what somebody typed, after the record did
 *     not take it, is the worst outcome available.
 */
import type { FieldMeta, ObjectMeta } from './metadata-source.js';
import type { ApiRecord, FetchImpl } from './content-source.js';

const DESCRIBE_PATH = '/api/data/describe';
const CREATE_PATH = '/api/data/create';
const UPDATE_PATH = '/api/data/update';

/** What THIS person may do with an object, and with each of its fields. */
export interface ObjectPermissions {
  readonly canCreate: boolean;
  readonly canUpdate: boolean;
  /** Bare field api name → whether this person may change it. */
  readonly editable: ReadonlySet<string>;
}

/** Nothing may be created or changed — the fail-safe when the Engine could not be asked. */
export const NO_WRITE: ObjectPermissions = { canCreate: false, canUpdate: false, editable: new Set() };

/**
 * Ask what this person may do with an object. On any failure the answer is NO — a read-only record
 * page is a small disappointment, whereas a form built on a guess invites somebody to type into a
 * field that will be thrown away.
 */
export async function loadObjectPermissions(
  objectKey: string,
  fetchImpl: FetchImpl = fetch,
): Promise<ObjectPermissions> {
  try {
    const res = await fetchImpl(`${DESCRIBE_PATH}?object=${encodeURIComponent(objectKey)}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const env = (await res.json()) as {
      ok?: boolean;
      data?: { canCreate?: boolean; canUpdate?: boolean; fields?: Array<{ apiName?: string; editable?: boolean }> };
    };
    if (env?.ok !== true || !env.data) return NO_WRITE;
    const editable = new Set<string>();
    for (const f of env.data.fields ?? []) {
      if (f.editable === true && typeof f.apiName === 'string') editable.add(f.apiName);
    }
    return { canCreate: env.data.canCreate === true, canUpdate: env.data.canUpdate === true, editable };
  } catch {
    return NO_WRITE;
  }
}

/**
 * CAOS-368 — the reserved key a record carries its optimistic-lock version under, spelled once so
 * the read, the write and the surface cannot disagree about it. It is the kernel's `VERSION_KEY`.
 */
export const RECORD_VERSION_KEY = 'version';

/**
 * The version a record was read at, or `undefined` when it arrived without one.
 *
 * Undefined is a real answer and not a failure: a record read from a substrate that does not track
 * versions has none, and a caller that sends no `expectedVersion` gets the pre-368 behaviour — the
 * route reads the current version and the save applies. So this never invents a number. Guessing
 * one would be worse than having none, because a wrong version refuses a save nobody conflicted on.
 */
export function recordVersion(record: ApiRecord | null | undefined): number | undefined {
  const raw = record?.[RECORD_VERSION_KEY];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

/**
 * The outcome of a write: the saved record, a lost race, or a sentence a person can act on.
 *
 * CAOS-368 — `conflict` is its own outcome rather than an `error` carrying a particular sentence,
 * because it is the one failure where the right thing to do next is known: the record moved on, so
 * show what it NOW says. Collapsed into `error`, a surface can only put the old value back and
 * repeat a message — which leaves the screen showing a value the record does not hold, the exact
 * outcome the rest of this module exists to prevent.
 */
export type WriteOutcome =
  | { readonly status: 'ok'; readonly record: ApiRecord }
  | { readonly status: 'conflict'; readonly message: string }
  | { readonly status: 'error'; readonly message: string };

/** The Engine's code for a save made against a version the record has already moved past. */
const STALE_VERSION_CODE = 'conflict.stale_version';

/**
 * Turn whatever the Engine said went wrong into one sentence for the person who pressed save. A
 * validation refusal is quoted (it is about THEIR value); anything else gets a plain statement
 * rather than a code, because a correlation id helps nobody standing at the screen.
 */
function failureMessage(env: unknown, fallback: string): string {
  const e = env as { error?: { message?: unknown; reason?: unknown; details?: unknown } } | null;
  const message = e?.error?.message;
  if (typeof message === 'string' && message.trim() !== '') return message;
  const reason = e?.error?.reason;
  if (typeof reason === 'string' && reason.trim() !== '') return reason;
  return fallback;
}

async function write(path: string, payload: unknown, fallback: string, fetchImpl: FetchImpl): Promise<WriteOutcome> {
  try {
    const res = await fetchImpl(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    const env = (await res.json()) as {
      ok?: boolean;
      data?: { record?: ApiRecord };
      error?: { code?: unknown };
    };
    if (env?.ok === true && env.data?.record) return { status: 'ok', record: env.data.record };
    // CAOS-368 — a lost race is told apart by the Engine's own CODE, not by matching the sentence
    // it came with. The message is a catalogue template an org can change; the code is the
    // contract. Its message is still carried through, because it is already the right thing to say.
    if (env?.error?.code === STALE_VERSION_CODE) {
      return { status: 'conflict', message: failureMessage(env, fallback) };
    }
    return { status: 'error', message: failureMessage(env, fallback) };
  } catch {
    return { status: 'error', message: fallback };
  }
}

/**
 * Change one or more fields on an existing record.
 *
 * CAOS-368 — `expectedVersion` is the version the edit STARTED FROM, and sending it is what turns
 * a silent overwrite into a refusal. Omitting it is still supported and still means what it always
 * meant: the route reads whatever the record is at now and the save applies — last write wins. So
 * a caller that has a version must pass it; one reading a substrate that has none is unchanged.
 */
export function updateRecord(
  objectKey: string,
  id: string,
  fields: Record<string, unknown>,
  expectedVersion?: number,
  fetchImpl: FetchImpl = fetch,
): Promise<WriteOutcome> {
  return write(
    UPDATE_PATH,
    // Omitted rather than sent as null/undefined: `data/update` refuses unknown fields and reads
    // `expectedVersion` only when it is a number, so an explicit undefined would be dead weight on
    // the wire at best and a rejected body at worst.
    {
      object: objectKey,
      id,
      fields,
      ...(typeof expectedVersion === 'number' ? { expectedVersion } : {}),
    },
    'That change could not be saved. Check your connection and try again.',
    fetchImpl,
  );
}

/**
 * Create a record.
 *
 * CAOS-1706 — `parentId` is the record the new one is ATTACHED to, stamped by the Engine on the
 * system `parent_id` column. It is NOT a field, so it cannot travel in `fields`: that map is the
 * object's authored columns and `data/create` refuses a name that is not one of them. The wire
 * field has existed since CAOS-367 and is create-only by design — re-parenting an existing record
 * is a separate decision, and an update that omits it keeps the prior value rather than detaching.
 *
 * Omitted rather than sent as null when there is no parent, for the reason `updateRecord` gives
 * about `expectedVersion`: the Engine reads a non-empty string, treats `null` as absent, and
 * refuses any other non-string.
 *
 * To be exact about why the spread rather than `parentId: parentId`: it is not that an explicit
 * `undefined` would be rejected — `JSON.stringify` drops undefined-valued keys, so it would never
 * reach the wire at all. It is that the body should SAY what it means. A key that is sometimes
 * present and sometimes silently dropped by the serialiser is a body whose shape depends on a
 * detail of `JSON.stringify`, and the next person reading the request log cannot tell an
 * unattached create from a bug in this function.
 */
export function createRecord(
  objectKey: string,
  fields: Record<string, unknown>,
  fetchImpl: FetchImpl = fetch,
  parentId?: string,
): Promise<WriteOutcome> {
  return write(
    CREATE_PATH,
    { object: objectKey, fields, ...(parentId ? { parentId } : {}) },
    'That could not be created. Check your connection and try again.',
    fetchImpl,
  );
}

/**
 * Whether a field can be OFFERED for editing at all: this person may change it, and it is a kind of
 * field a person fills in. A formula, a rollup and an auto-number are computed by the Engine — they
 * are read-only to everybody, and the Engine says so too; this is the surface agreeing rather than
 * relying on it.
 */
export function isEditable(field: FieldMeta, perms: ObjectPermissions): boolean {
  if (!perms.canUpdate) return false;
  if (field.storageType === 'Formula' || field.storageType === 'Rollup' || field.storageType === 'AutoNumber') {
    return false;
  }
  // CAOS-365 — a system field (when the record was created, when it last changed) is kept by the
  // platform. There is no column behind it a person could fill in, so it is never offered.
  if (field.system) return false;
  return perms.editable.has(field.apiName);
}

/**
 * The value to SEND for a field, from what the control produced. The wire wants a canonical value,
 * and an empty box means "clear this", not the empty string — a Number field given `''` would be a
 * type error, and a cleared Text field given `''` would read back as a value that is there and blank
 * rather than a field nobody has filled in.
 */
export function wireValue(field: FieldMeta, raw: unknown): unknown {
  if (raw === '' || raw === undefined) return null;
  if (field.storageType === 'Number' && typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return raw;
}

/** The value to PUT IN a control, from the record as it arrived. Money arrives as `{amount,currency}`. */
export function controlValue(field: FieldMeta, record: ApiRecord): unknown {
  const raw = record[field.apiName];
  if (raw != null && typeof raw === 'object' && 'amount' in (raw as Record<string, unknown>)) {
    const amount = (raw as { amount: unknown }).amount;
    const n = typeof amount === 'number' ? amount : Number(amount);
    return Number.isFinite(n) ? n : null;
  }
  return raw ?? null;
}

/**
 * The fields of a record's layout, in layout order, that this person may fill in.
 *
 * CAOS-360 — both answers have to agree, and they answer different questions. `offeredForEdit` is
 * the SURFACE's: this layout asked for the field to be editable. `isEditable` is the ENGINE's: this
 * person may actually write it. A create form built from this therefore offers exactly the fields
 * the layout put forward AND the boundary would accept — never one it would silently strip, and
 * never one the surface deliberately declined.
 */
export function editableFields(object: ObjectMeta, perms: ObjectPermissions): FieldMeta[] {
  const out: FieldMeta[] = [];
  for (const section of object.sections) {
    for (const placement of section.fields) {
      if (!placement.offeredForEdit) continue;
      const f = object.fields.get(placement.name);
      if (f && isEditable(f, perms)) out.push(f);
    }
  }
  return out;
}
